import Modal from '@/components/Modal';
import { getServerApi } from '@/gql';
import { extractGraphQLErrorMessage } from '@/utils';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '../Button';

const RedeemContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
    padding: 40px 0 0 0;
    width: 100%;
    width: 400px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
   width: 100%;
    `};
`;

const InputContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const CodeInput = styled.input<{ hasError: boolean }>`
    width: 100%;
    max-width: 400px;
    height: 48px;
    border: 2px solid
        ${({ hasError }) => (hasError ? '#ff4444' : 'rgba(53, 99, 243, 0.1)')};
    border-radius: 8px;
    text-align: center;
    font-size: 18px;
    padding: 0 16px;

    background: white;
    color: #000;
    outline: none;
    transition: all 0.2s ease;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        height: 44px;
        font-size: 16px;
    `};

    &:focus {
        border-color: ${({ hasError }) => (hasError ? '#ff4444' : '#2563eb')};
        box-shadow: 0 0 0 3px
            ${({ hasError }) =>
                hasError
                    ? 'rgba(255, 68, 68, 0.1)'
                    : 'rgba(59, 130, 246, 0.1)'};
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const ErrorMessage = styled.div`
    color: #ff4444;
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    margin-top: -8px;
    width: 100%;
    text-align: left;
`;

const ConfirmButton = styled(Button)`
    width: 100%;

    height: 48px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:disabled {
        background: #9ca3af;
        cursor: not-allowed;
    }
`;

const InputWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
`;

interface RedeemModalProps {
    isOpen: boolean;
    onClose: () => void;
    confirm?: (code: string) => Promise<boolean>;
    handleSuccess?: (code: string) => void;
    handleError?: (error: string) => void;
}

const RedeemModal: React.FC<RedeemModalProps> = ({
    isOpen,
    onClose,
    confirm,
    handleSuccess,
    handleError,
}) => {
    const [code, setCode] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // 重置状态
    useEffect(() => {
        if (isOpen) {
            setCode('');
            setError('');
            setIsSubmitting(false);
            // 聚焦到输入框
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const handleInputChange = (value: string) => {
        // 只允许数字和字母
        const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '');
        setCode(sanitizedValue);
        setError(''); // 清除错误信息
    };

    const handleSubmit = async () => {
        if (code.trim().length === 0) {
            setError('Please enter the activation code');
            return;
        }

        setIsSubmitting(true);

        setError('');

        try {
            const success = await getServerApi().reedemAcitvationCode({
                activationCode: code.trim(),
            });

            if (!success) {
                setError('Invalid code');
                handleError?.('Invalid code');
                return;
            }

            handleSuccess?.(code.trim());
            onClose();
        } catch (err) {
            const errorMessage = extractGraphQLErrorMessage(err);
            setError(errorMessage);
            handleError?.(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isCodeComplete = code.trim().length > 0;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Redeem code">
            <RedeemContainer>
                <InputWrapper>
                    <InputContainer>
                        <CodeInput
                            ref={inputRef}
                            type="text"
                            value={code}
                            onChange={(e) => handleInputChange(e.target.value)}
                            hasError={!!error}
                            disabled={isSubmitting}
                            placeholder="Enter activation code"
                        />
                    </InputContainer>

                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </InputWrapper>

                <ConfirmButton
                    onClick={handleSubmit}
                    disabled={!isCodeComplete || isSubmitting}
                    type="primary"
                    size="normal"
                    auth
                    showLoading={false}
                >
                    {isSubmitting ? 'Confirming...' : 'Confirm'}
                </ConfirmButton>
            </RedeemContainer>
        </Modal>
    );
};

export default RedeemModal;
